import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCollaboration, useCollaborationMutations } from '../api/hooks'
import { useAuditors } from '../api/admin'
import type { CollaboratorDto, FormInstanceStatus } from '../api/types'
import { problemOf } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal } from './ui'

const initialsOf = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const letters = parts.length > 1 ? [parts[0], parts[parts.length - 1]] : parts
  return letters.map((p) => p.charAt(0).toLocaleUpperCase('tr-TR')).join('') || '?'
}

// Stable avatar colour per person, readable with white text in both themes.
const AVATAR_COLORS = ['#1f5fbf', '#1d6f42', '#b4531a', '#7a4fd6', '#0f7b8a', '#a8326b', '#5a6b1f', '#8a5a00']
const colorOf = (key: string) => {
  let hash = 0
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

export function Avatar({ name, id, size = 30 }: { name: string; id?: string; size?: number }) {
  return <span className="avatar" aria-hidden="true" style={{ width: size, height: size, fontSize: size * 0.4, background: colorOf(id ?? name) }}>{initialsOf(name)}</span>
}

/**
 * Who works on the report: the owner and the approved participants, with a green "canlı" dot while the screen is polling for changes.
 * The owner (or a tenant admin) also sees join requests (Onayla / Reddet), adds people directly and removes them; a participant can leave.
 * Management is offered while the report is a draft; afterwards the panel only lists the people (and hides itself when nobody joined).
 */
export default function CollaborationPanel({ reportId, status, live = false }: { reportId: string; status: FormInstanceStatus; live?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const collab = useCollaboration(reportId)
  const m = useCollaborationMutations(reportId)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const c = collab.data
  if (!c) return null

  const isDraft = status === 'Draft'
  // The server treats a report without a known creator as everybody's; managing people still needs an editing role.
  const isEditor = !!user?.roles.some((x) => ['SuperAdmin', 'TenantAdmin', 'Auditor'].includes(x.role))
  const canManage = c.isOwner && isDraft && isEditor
  const members = c.people.filter((p) => p.role === 'Owner' || p.status === 'Approved')
  const pending = canManage ? c.people.filter((p) => p.role === 'Collaborator' && p.status === 'Pending') : []
  const rejected = canManage ? c.people.filter((p) => p.role === 'Collaborator' && p.status === 'Rejected') : []
  const mine = c.people.find((p) => p.userId === user?.id && p.role === 'Collaborator' && p.status === 'Approved')
  const hasParticipants = members.some((p) => p.role === 'Collaborator')
  // Nothing to show or do on a finished report nobody joined.
  if (!isDraft && !hasParticipants && pending.length === 0) return null

  const busy = m.approve.isPending || m.reject.isPending || m.remove.isPending
  const onError = (e: unknown) => setError(problemOf(e).title ?? t('common.error'))
  const run = (mutation: typeof m.approve, userId: string) => { setError(null); mutation.mutate(userId, { onError }) }

  function remove(p: CollaboratorDto) {
    if (!confirm(t('collab.removeConfirm', { name: p.fullName }))) return
    setError(null)
    m.remove.mutate(p.userId, { onError })
  }
  function leave() {
    if (!mine || !confirm(t('collab.leaveConfirm'))) return
    setError(null)
    m.remove.mutate(mine.userId, { onSuccess: () => navigate('/reports'), onError })
  }

  return (
    <section className="card collab-panel" aria-label={t('collab.title')}>
      <div className="collab-head">
        <div className="row wrap" style={{ gap: 8 }}>
          <h3 style={{ margin: 0 }}>{t('collab.title')}</h3>
          <span className="muted small">{t('collab.peopleCount', { count: members.length })}</span>
          {pending.length > 0 ? <span className="count-badge" title={t('collab.pendingCount', { count: pending.length })} aria-label={t('collab.pendingCount', { count: pending.length })}>{pending.length}</span> : null}
          {live ? <span className="live-indicator" title={t('collab.liveHint')}><span className="live-dot" />{t('collab.live')}</span> : null}
        </div>
        <div className="row wrap collab-actions">
          {canManage ? <button type="button" className="secondary small-btn" onClick={() => { setError(null); setInviteOpen(true) }}>{t('collab.addPerson')}</button> : null}
          {mine && isDraft ? <button type="button" className="ghost small-btn collab-leave" onClick={leave} disabled={busy}>{t('collab.leave')}</button> : null}
        </div>
      </div>

      <ul className="collab-people">
        {members.map((p) => (
          <li key={p.userId} className="collab-person" title={p.email}>
            <Avatar name={p.fullName} id={p.userId} />
            <span className="collab-name">{p.fullName}{p.userId === user?.id ? <span className="muted"> ({t('collab.you')})</span> : null}</span>
            {p.role === 'Owner' ? <Badge color="#1f5fbf">{t('collab.owner')}</Badge> : null}
            {canManage && p.role === 'Collaborator' ? (
              <button type="button" className="ghost collab-remove" onClick={() => remove(p)} disabled={busy} aria-label={t('collab.removeNamed', { name: p.fullName })} title={t('collab.remove')}>×</button>
            ) : null}
          </li>
        ))}
      </ul>
      {isDraft && !hasParticipants && canManage ? <p className="muted small" style={{ margin: 0 }}>{t('collab.aloneHint')}</p> : null}

      {pending.length > 0 ? (
        <div className="collab-requests">
          <div className="small"><b>{t('collab.requests')}</b></div>
          {pending.map((p) => (
            <div key={p.userId} className="collab-request">
              <div className="row" style={{ minWidth: 0 }}>
                <Avatar name={p.fullName} id={p.userId} />
                <div style={{ minWidth: 0 }}>
                  <div className="collab-name">{p.fullName}</div>
                  <div className="muted small collab-email">{p.email}</div>
                </div>
              </div>
              <div className="row collab-request-actions">
                <button type="button" className="small-btn" onClick={() => run(m.approve, p.userId)} disabled={busy}>{t('collab.approve')}</button>
                <button type="button" className="secondary small-btn" onClick={() => run(m.reject, p.userId)} disabled={busy}>{t('collab.reject')}</button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {rejected.length > 0 ? (
        <details className="collab-rejected">
          <summary className="muted small">{t('collab.rejectedCount', { count: rejected.length })}</summary>
          {rejected.map((p) => (
            <div key={p.userId} className="collab-request">
              <div className="row" style={{ minWidth: 0 }}><Avatar name={p.fullName} id={p.userId} size={24} /><span className="collab-name small">{p.fullName}</span></div>
              <button type="button" className="secondary small-btn" onClick={() => run(m.approve, p.userId)} disabled={busy}>{t('collab.approveAnyway')}</button>
            </div>
          ))}
        </details>
      ) : null}

      {error ? <p className="error small" role="alert" style={{ margin: 0 }}>{error}</p> : null}
      {inviteOpen ? <InviteModal reportId={reportId} people={c.people} onClose={() => setInviteOpen(false)} /> : null}
    </section>
  )
}

/** "Kişi ekle": the tenant's auditors minus the people already on the report (or waiting for approval). */
function InviteModal({ reportId, people, onClose }: { reportId: string; people: CollaboratorDto[]; onClose: () => void }) {
  const { t } = useTranslation()
  const auditors = useAuditors()
  const { invite } = useCollaborationMutations(reportId)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const taken = new Set(people.filter((p) => p.role === 'Owner' || p.status !== 'Rejected').map((p) => p.userId))
  const needle = search.trim().toLocaleLowerCase('tr-TR')
  const candidates = (auditors.data ?? [])
    .filter((a) => !taken.has(a.id))
    .filter((a) => needle === '' || `${a.fullName} ${a.email} ${a.title ?? ''}`.toLocaleLowerCase('tr-TR').includes(needle))

  function add(userId: string) {
    setError(null)
    invite.mutate(userId, { onError: (e) => setError(problemOf(e).title ?? t('common.error')) })
  }

  return (
    <Modal title={t('collab.addPerson')} onClose={onClose} width={480}>
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>{t('collab.inviteHint')}</p>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common.search')} autoComplete="off" aria-label={t('common.search')} />
        {auditors.isLoading ? <p className="muted">{t('common.loading')}</p> : auditors.isError ? <p className="error small">{t('common.loadFailed')}</p> : candidates.length === 0 ? <Empty text={t('collab.noCandidates')} /> : (
          <div className="collab-candidates">
            {candidates.map((a) => (
              <div key={a.id} className="collab-request">
                <div className="row" style={{ minWidth: 0 }}>
                  <Avatar name={a.fullName} id={a.id} />
                  <div style={{ minWidth: 0 }}>
                    <div className="collab-name">{a.fullName}</div>
                    <div className="muted small collab-email">{a.title ? `${a.title} · ` : ''}{a.email}</div>
                  </div>
                </div>
                <button type="button" className="small-btn" onClick={() => add(a.id)} disabled={invite.isPending}>{invite.isPending && invite.variables === a.id ? t('common.pleaseWait') : t('collab.add')}</button>
              </div>
            ))}
          </div>
        )}
        {error ? <p className="error small" role="alert" style={{ margin: 0 }}>{error}</p> : null}
        <div className="row" style={{ justifyContent: 'flex-end' }}><button type="button" className="secondary" onClick={onClose}>{t('collab.done')}</button></div>
      </div>
    </Modal>
  )
}
